import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { t } from 'ttag';
import { operatorName } from '../../../../config/operator';
import useOperatorContext from '../../../../contexts/operator/useOperatorContext';
import { TSite } from '../../../../contexts/site/site-context';
import { validatePostcode } from '../../../../services/api/site';
import { CTA } from '../../../general/Button';
import ModalPanel from './ModalPanel';
import { ErrorMessage, Question } from './styles';
import { trackSiteSelectComplete } from '../../../../services/siteSelectTracking';

type DeliveryPanelProps = {
    channel: 'delivery' | 'catering';
    focusRequest?: number;
};

const DeliveryPanel = ({ channel, focusRequest }: DeliveryPanelProps) => {
    const navigate = useNavigate();
    const operatorContext = useOperatorContext();

    const [postcode, setPostcode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const postcodeInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        postcodeInputRef.current?.focus();
    }, []);

    const ready = postcode.trim() !== '' && !loading && error === '';

    const start = () => {
        setLoading(true);

        validatePostcode(operatorName, postcode, operatorContext.operator?.flow ?? 'noflow', channel)
            .then((sites: TSite[]) => {
                // The full address is picked later, at checkout
                localStorage.setItem('address', JSON.stringify({ postcode: postcode.toUpperCase() }));

                localStorage.setItem('postcode', postcode.toUpperCase());

                trackSiteSelectComplete('modal', channel, sites.length === 1 ? sites[0].handle : undefined);

                if (sites.length === 1) {
                    navigate(`/${channel}/${sites[0].handle}`);
                } else {
                    navigate(`/${channel}`);
                    setLoading(false);
                }
            })
            .catch((reason: any) => {
                localStorage.removeItem('address');
                localStorage.removeItem('postcode');

                // 803: nowhere delivers to the postcode
                if (reason.code === 803) {
                    setError(reason.message);
                }

                setLoading(false);
            });
    };

    const changePostcode = (value: string) => {
        setError('');
        setPostcode(value);
    };

    return (
        <ModalPanel
            focusRequest={focusRequest}
            footer={
                <CTA block disabled={!ready} loading={loading} onClick={start}>
                    <span>{t`View menu`}</span>
                </CTA>
            }
        >
            <Question>{channel === 'catering' ? t`Where’s the catering order going?` : t`Where should we deliver?`}</Question>

            <InputWrapper>
                <div className="postcode-input-wrapper">
                    <Form.Control
                        size="lg"
                        type="text"
                        className="postcode-input"
                        aria-label={t`Postcode`}
                        placeholder={t`Enter the delivery postcode`}
                        autoComplete="postal-code"
                        value={postcode}
                        onChange={(e) => changePostcode(e.target.value)}
                        onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && ready && start()}
                        ref={postcodeInputRef}
                    />
                    <span className="material-symbols-outlined postcode-input__search" aria-hidden="true">search</span>
                </div>
            </InputWrapper>

            { error !== '' && (
                <ErrorMessage role="alert">
                    <span className="material-symbols-outlined" aria-hidden="true">info</span>
                    {error}
                </ErrorMessage>
            )}
        </ModalPanel>
    );
};

const InputWrapper = styled.div`
    position: relative;

    .postcode-input-wrapper {
        position: relative;
    }

    .postcode-input__search {
        position: absolute;
        top: 8px;
        left: 12px;
        padding: 4px;
        border-radius: var(--radius_m);
        color: var(--text-black);
        font-size: 24px;
        line-height: 24px;
        pointer-events: none;
    }

    .form-control {
        padding: 18px 16px 18px 48px;
        margin-bottom: 0;
    }
`;

export default DeliveryPanel;
